import { RelationshipTypeEnum } from '@/types/generated'

/**
 * German accusative phrases for the "add another" prompt.
 * Format: "einen weiteren X" (m), "eine weitere X" (f), "ein weiteres X" (n)
 *
 * This handles:
 * - Correct grammatical gender (masculine/feminine/neuter)
 * - Accusative case declension for nominalized adjectives (e.g., Angeklagter → Angeklagten)
 * - Weak noun declension (e.g., Bürge → Bürgen, Zeuge → Zeugen)
 */
export const entityAccusativeDE: Record<string, string> = {
  // Masculine nouns with standard accusative
  [RelationshipTypeEnum.ENTITIES_CLIENT]: 'einen weiteren Mandanten',
  [RelationshipTypeEnum.ENTITIES_OPPONENT]: 'einen weiteren Gegner',
  [RelationshipTypeEnum.ENTITIES_LABOR_LAW_EMPLOYEE]: 'einen weiteren Arbeitnehmer',
  [RelationshipTypeEnum.ENTITIES_LABOR_LAW_EMPLOYER]: 'einen weiteren Arbeitgeber',
  [RelationshipTypeEnum.ENTITIES_AT_FAULT_DRIVER]: 'einen weiteren Unfallverursacher',
  [RelationshipTypeEnum.ENTITIES_PLAINTIFF]: 'einen weiteren Kläger',
  [RelationshipTypeEnum.ENTITIES_VEHICLE_OWNER]: 'einen weiteren Fahrzeughalter',
  [RelationshipTypeEnum.ENTITIES_DRIVER]: 'einen weiteren Fahrer',
  [RelationshipTypeEnum.ENTITIES_JUDGE]: 'einen weiteren Richter',
  [RelationshipTypeEnum.ENTITIES_PROSECUTOR]: 'einen weiteren Staatsanwalt',
  [RelationshipTypeEnum.ENTITIES_CO_PLAINTIFF]: 'einen weiteren Mitkläger',
  [RelationshipTypeEnum.ENTITIES_TENANT]: 'einen weiteren Mieter',
  [RelationshipTypeEnum.ENTITIES_LANDLORD]: 'einen weiteren Vermieter',
  [RelationshipTypeEnum.ENTITIES_SUBTENANT]: 'einen weiteren Untermieter',
  [RelationshipTypeEnum.ENTITIES_OWNER]: 'einen weiteren Eigentümer',
  [RelationshipTypeEnum.ENTITIES_REAL_ESTATE_AGENT]: 'einen weiteren Immobilienmakler',
  [RelationshipTypeEnum.ENTITIES_CO_OWNER]: 'einen weiteren Miteigentümer',
  [RelationshipTypeEnum.ENTITIES_CO_TENANT]: 'einen weiteren Mitmieter',
  [RelationshipTypeEnum.ENTITIES_COMPLAINANT]: 'einen weiteren Anzeigenerstatter',
  [RelationshipTypeEnum.ENTITIES_APPLICANT]: 'einen weiteren Antragsteller',
  [RelationshipTypeEnum.ENTITIES_RESPONDENT]: 'einen weiteren Antwortsteller',
  [RelationshipTypeEnum.ENTITIES_PRINCIPAL_CLIENT]: 'einen weiteren Auftraggeber',
  [RelationshipTypeEnum.ENTITIES_HUSBAND]: 'einen weiteren Ehemann',
  [RelationshipTypeEnum.ENTITIES_KORRESPONDENCE_LAWYER]: 'einen weiteren Korrespondenzanwalt',
  [RelationshipTypeEnum.ENTITIES_OPPOSING_LAWYER]: 'einen weiteren Gegenanwalt',
  [RelationshipTypeEnum.ENTITIES_LAWYER]: 'einen weiteren Anwalt',
  [RelationshipTypeEnum.ENTITIES_CONTACT_PERSON]: 'einen weiteren Ansprechpartner',
  [RelationshipTypeEnum.ENTITIES_THIRD_PARTY_DEBTOR]: 'einen weiteren Drittschuldner',
  [RelationshipTypeEnum.ENTITIES_BUYER]: 'einen weiteren Käufer',
  [RelationshipTypeEnum.ENTITIES_SELLER]: 'einen weiteren Verkäufer',
  [RelationshipTypeEnum.ENTITIES_ADMINISTRATOR]: 'einen weiteren Verwalter',
  [RelationshipTypeEnum.ENTITIES_SCHULDNER]: 'einen weiteren Schuldner',
  [RelationshipTypeEnum.ENTITIES_DEBTOR]: 'einen weiteren Schuldner',
  [RelationshipTypeEnum.ENTITIES_CREDITOR]: 'einen weiteren Gläubiger',
  [RelationshipTypeEnum.ENTITIES_LESSEE]: 'einen weiteren Pächter',
  [RelationshipTypeEnum.ENTITIES_LESSOR]: 'einen weiteren Verpächter',
  [RelationshipTypeEnum.ENTITIES_PRINCIPAL]: 'einen weiteren Vollmachtgeber',
  [RelationshipTypeEnum.ENTITIES_TESTATOR]: 'einen weiteren Erblasser',
  [RelationshipTypeEnum.ENTITIES_CURATOR_OF_THE_ESTATE]: 'einen weiteren Nachlasspfleger',
  [RelationshipTypeEnum.ENTITIES_EXECUTOR_OF_THE_WILL]: 'einen weiteren Testamentsvollstrecker',
  [RelationshipTypeEnum.ENTITIES_ESTATE_ADMINISTRATOR]: 'einen weiteren Nachlassverwalter',
  [RelationshipTypeEnum.ENTITIES_SECONDARY_CLIENT]: 'einen weiteren sekundären Mandanten',
  [RelationshipTypeEnum.ENTITIES_SECONDARY_OPPONENT]: 'einen weiteren sekundären Gegner',
  [RelationshipTypeEnum.ENTITIES_INVESTIGATOR]: 'einen weiteren Ermittler',
  [RelationshipTypeEnum.ENTITIES_BAILIFF]: 'einen weiteren Gerichtsvollzieher',
  [RelationshipTypeEnum.ENTITIES_NOTARY]: 'einen weiteren Notar',
  [RelationshipTypeEnum.ENTITIES_TRUSTEE]: 'einen weiteren Treuhänder',
  [RelationshipTypeEnum.ENTITIES_INSOLVENCY_ADMINISTRATOR]: 'einen weiteren Insolvenzverwalter',
  [RelationshipTypeEnum.ENTITIES_DEFENDANT_REPRESENTATIVE]: 'einen weiteren Beklagtenvertreter',
  [RelationshipTypeEnum.ENTITIES_PLAINTIFF_REPRESENTATIVE]: 'einen weiteren Klägervertreter',
  [RelationshipTypeEnum.ENTITIES_FRACTIONAL_OWNER]: 'einen weiteren Bruchteilseigentümer',
  [RelationshipTypeEnum.ENTITIES_JOINT_DEBTOR]: 'einen weiteren Gesamtschuldner',
  [RelationshipTypeEnum.ENTITIES_JOINT_CREDITOR]: 'einen weiteren Gesamtgläubiger',
  [RelationshipTypeEnum.ENTITIES_APPLICATION_RESPONDENT]: 'einen weiteren Antragsgegner',
  [RelationshipTypeEnum.ENTITIES_HEREDITARY_BUILDING_GRANTOR]: 'einen weiteren Erbbaugeber',
  [RelationshipTypeEnum.ENTITIES_SPECIAL_PROPERTY_MANAGER]:
    'einen weiteren Sondereigentumsverwalter',
  [RelationshipTypeEnum.ENTITIES_HOUSE_MANAGER]: 'einen weiteren Hausverwalter',
  [RelationshipTypeEnum.ENTITIES_APPELLANT]: 'einen weiteren Berufungskläger',
  [RelationshipTypeEnum.ENTITIES_COUNTER_CLAIMANT]: 'einen weiteren Widerkläger',
  [RelationshipTypeEnum.ENTITIES_DOCTOR]: 'einen weiteren Arzt',
  [RelationshipTypeEnum.ENTITIES_GUARDIAN]: 'einen weiteren Vormund',
  [RelationshipTypeEnum.ENTITIES_CARETAKER]: 'einen weiteren Betreuer',
  [RelationshipTypeEnum.ENTITIES_MAINTENANCE_OBLIGOR]: 'einen weiteren Unterhaltspflichtigen',
  [RelationshipTypeEnum.ENTITIES_ADMINISTRATIVE_ASSISTANT]: 'einen weiteren Verwaltungshelfer',
  [RelationshipTypeEnum.ENTITIES_CASE_WORKER]: 'einen weiteren Sachbearbeiter',
  [RelationshipTypeEnum.ENTITIES_MANAGING_DIRECTOR]: 'einen weiteren Geschäftsführer',
  [RelationshipTypeEnum.ENTITIES_SHAREHOLDER]: 'einen weiteren Gesellschafter',
  [RelationshipTypeEnum.ENTITIES_COMMERCIAL_AGENT]: 'einen weiteren Handelsvertreter',
  [RelationshipTypeEnum.ENTITIES_LIMITED_PARTNER]: 'einen weiteren Kommanditisten',
  [RelationshipTypeEnum.ENTITIES_AUTHORIZED_SIGNATORY]: 'einen weiteren Prokuristen',
  [RelationshipTypeEnum.ENTITIES_REHABILITATION_PROVIDER]: 'einen weiteren Rehabilitationsträger',
  [RelationshipTypeEnum.ENTITIES_LEGAL_ASSISTANT]: 'einen weiteren Verfahrensbeistand',
  [RelationshipTypeEnum.ENTITIES_OFFICIANT]: 'einen weiteren Amtsträger',
  [RelationshipTypeEnum.ENTITIES_PROBATION_OFFICER]: 'einen weiteren Bewährungshelfer',

  // Masculine nouns - weak declension (add -n in accusative)
  [RelationshipTypeEnum.ENTITIES_WITNESS]: 'einen weiteren Zeugen',
  [RelationshipTypeEnum.ENTITIES_GUARANTOR]: 'einen weiteren Bürgen',
  [RelationshipTypeEnum.ENTITIES_HEIR]: 'einen weiteren Erben',
  [RelationshipTypeEnum.ENTITIES_PRELIMINARY_HEIR]: 'einen weiteren Vorerben',
  [RelationshipTypeEnum.ENTITIES_SUBSEQUENT_HEIR]: 'einen weiteren Nacherben',
  [RelationshipTypeEnum.ENTITIES_PATIENT]: 'einen weiteren Patienten',

  // Masculine nouns - nominalized adjectives (change -er to -en in accusative)
  [RelationshipTypeEnum.ENTITIES_ACCUSED]: 'einen weiteren Beschuldigten',
  [RelationshipTypeEnum.ENTITIES_DEFENDANT]: 'einen weiteren Angeklagten',
  [RelationshipTypeEnum.ENTITIES_COMPLAINT]: 'einen weiteren Beklagten',
  [RelationshipTypeEnum.ENTITIES_VICTIM]: 'einen weiteren Geschädigten',
  [RelationshipTypeEnum.ENTITIES_VEHICLE_EXPERT]: 'einen weiteren KFZ-Sachverständigen',
  [RelationshipTypeEnum.ENTITIES_POLICE_OFFICER]: 'einen weiteren Polizeibeamten',
  [RelationshipTypeEnum.ENTITIES_EXPERT]: 'einen weiteren Sachverständigen',
  [RelationshipTypeEnum.ENTITIES_HEREDITARY_LEASEHOLDER]: 'einen weiteren Erbbauberechtigten',
  [RelationshipTypeEnum.ENTITIES_AUTHORIZED_REPRESENTATIVE]: 'einen weiteren Bevollmächtigten',
  [RelationshipTypeEnum.ENTITIES_COMPULSORY_PORTION_BENEFICIARY]:
    'einen weiteren Pflichtteilsberechtigten',
  [RelationshipTypeEnum.ENTITIES_APPELLEE]: 'einen weiteren Berufungsbeklagten',
  [RelationshipTypeEnum.ENTITIES_COUNTER_DEFENDANT]: 'einen weiteren Widerbeklagten',
  [RelationshipTypeEnum.ENTITIES_LEGAL_GUARDIAN]: 'einen weiteren Sorgeberechtigten',
  [RelationshipTypeEnum.ENTITIES_BENEFICIARY]: 'einen weiteren Leistungsberechtigten',
  [RelationshipTypeEnum.ENTITIES_AFFECTED_PERSON]: 'einen weiteren Betroffenen',
  [RelationshipTypeEnum.ENTITIES_VICTIM_DRIVER]: 'einen weiteren geschädigten Fahrer',
  [RelationshipTypeEnum.ENTITIES_OPPOSING_COUNSEL]: 'einen weiteren gegnerischen Anwalt',
  [RelationshipTypeEnum.ENTITIES_LEGAL_REPRESENTATIVE]: 'einen weiteren gesetzlichen Vertreter',

  // Feminine nouns (eine weitere)
  [RelationshipTypeEnum.ENTITIES_AGENCY]: 'eine weitere Behörde',
  [RelationshipTypeEnum.ENTITIES_FOREIGNERS_AUTHORITY]: 'eine weitere Ausländerbehörde',
  [RelationshipTypeEnum.ENTITIES_INSURANCE]: 'eine weitere Versicherung',
  [RelationshipTypeEnum.ENTITIES_POLICE_STATION]: 'eine weitere Polizeidienststelle',
  [RelationshipTypeEnum.ENTITIES_PUBLIC_PROSECUTOR]: 'eine weitere Staatsanwaltschaft',
  [RelationshipTypeEnum.ENTITIES_PROPERTY_MANAGEMENT]: 'eine weitere Hausverwaltung',
  [RelationshipTypeEnum.ENTITIES_TENANTS_ASSOCIATION]: 'einen weiteren Mieterverein',
  [RelationshipTypeEnum.ENTITIES_PROPERTY_OWNERS_ASSOCIATION]:
    'eine weitere Eigentümergemeinschaft',
  [RelationshipTypeEnum.ENTITIES_WIFE]: 'eine weitere Ehefrau',
  [RelationshipTypeEnum.ENTITIES_LEGAL_EXPENSES_INSURANCE]: 'eine weitere Rechtsschutzversicherung',
  [RelationshipTypeEnum.ENTITIES_LIABILITY_INSURANCE]: 'eine weitere Haftpflichtversicherung',
  [RelationshipTypeEnum.ENTITIES_BANK]: 'eine weitere Bank',
  [RelationshipTypeEnum.ENTITIES_GENERAL_PROSECUTOR]: 'eine weitere Generalstaatsanwaltschaft',
  [RelationshipTypeEnum.ENTITIES_MUNICIPALITY]: 'eine weitere Gemeinde',
  [RelationshipTypeEnum.ENTITIES_COURT_COMPLAINT]: 'eine weitere Gerichtsbeschwerde',
  [RelationshipTypeEnum.ENTITIES_FINE_AUTHORITY]: 'eine weitere Bußgeldbehörde',
  [RelationshipTypeEnum.ENTITIES_COMMUNITY_OF_HOMEOWNERS]:
    'eine weitere Gemeinschaft der Wohnungseigentümer (GdWE)',
  [RelationshipTypeEnum.ENTITIES_PROFESSIONAL_LIABILITY_INSURANCE]:
    'eine weitere Berufshaftpflichtversicherung',
  [RelationshipTypeEnum.ENTITIES_BUILDING_OWNERS_ASSOCIATION]: 'eine weitere Bauherrengemeinschaft',
  [RelationshipTypeEnum.ENTITIES_LANDLORD_ASSOCIATION]: 'einen weiteren Vermieterverein',
  [RelationshipTypeEnum.ENTITIES_LANDLORD_COMMUNITY]: 'eine weitere Vermietergemeinschaft',
  [RelationshipTypeEnum.ENTITIES_FOSTER_PARENT]: 'eine weitere Pflegeperson',
  [RelationshipTypeEnum.ENTITIES_PENSION_INSURANCE]: 'eine weitere Rentenversicherung',
  [RelationshipTypeEnum.ENTITIES_HEALTH_INSURANCE]: 'eine weitere Krankenkasse',
  [RelationshipTypeEnum.ENTITIES_TAX_AUTHORITY]: 'eine weitere Finanzbehörde',
  [RelationshipTypeEnum.ENTITIES_OPPONENT_INSURANCE]: 'eine weitere gegnerische Versicherung',
  [RelationshipTypeEnum.ENTITIES_MEDICAL_INSTITUTION]: 'eine weitere medizinische Einrichtung',
  [RelationshipTypeEnum.ENTITIES_WORKSHOP]: 'eine weitere Werkstatt',

  // Neuter nouns (ein weiteres)
  [RelationshipTypeEnum.ENTITIES_OTHER]: 'einen weiteren Sonstigen',
  [RelationshipTypeEnum.ENTITIES_COURT]: 'ein weiteres Gericht',
  [RelationshipTypeEnum.ENTITIES_FIRST_INSTANCE_COURT]: 'ein weiteres Gericht 1. Instanz',
  [RelationshipTypeEnum.ENTITIES_SECOND_INSTANCE_COURT]: 'ein weiteres Gericht 2. Instanz',
  [RelationshipTypeEnum.ENTITIES_THIRD_INSTANCE_COURT]: 'ein weiteres Gericht 3. Instanz',
  [RelationshipTypeEnum.ENTITIES_TAX_OFFICE]: 'ein weiteres Finanzamt',
  [RelationshipTypeEnum.ENTITIES_LAND_REGISTRY]: 'ein weiteres Grundbuchamt',
  [RelationshipTypeEnum.ENTITIES_DUNNING_COURT]: 'ein weiteres Mahngericht',
  [RelationshipTypeEnum.ENTITIES_ENFORCEMENT_COURT]: 'ein weiteres Vollstreckungsgericht',
  [RelationshipTypeEnum.ENTITIES_REGISTER_COURT]: 'ein weiteres Registergericht',
  [RelationshipTypeEnum.ENTITIES_PROBATE_COURT]: 'ein weiteres Nachlassgericht',
  [RelationshipTypeEnum.ENTITIES_REGISTRY_OFFICE]: 'ein weiteres Standesamt',
  [RelationshipTypeEnum.ENTITIES_LEGAL_AID_COURT]: 'ein weiteres Beratungshilfegericht',
  [RelationshipTypeEnum.ENTITIES_RESIDENT_REGISTRATION_OFFICE]: 'ein weiteres Einwohnermeldeamt',
  [RelationshipTypeEnum.ENTITIES_INSOLVENCY_COURT]: 'ein weiteres Insolvenzgericht',
  [RelationshipTypeEnum.ENTITIES_INTEGRATION_OFFICE]: 'ein weiteres Integrationsamt',
  [RelationshipTypeEnum.ENTITIES_EXECUTION_ORGAN]: 'ein weiteres Vollstreckungsorgan',
  [RelationshipTypeEnum.ENTITIES_YOUTH_WELFARE_OFFICE]: 'ein weiteres Jugendamt',
  [RelationshipTypeEnum.ENTITIES_SOCIAL_SECURITY_OFFICE]: 'ein weiteres Sozialamt',
  [RelationshipTypeEnum.ENTITIES_JOB_CENTER]: 'ein weiteres Jobcenter',
  [RelationshipTypeEnum.ENTITIES_DISABILITY_OFFICE]: 'ein weiteres Versorgungsamt',
  [RelationshipTypeEnum.ENTITIES_SOCIAL_COURT]: 'ein weiteres Sozialgericht',
  [RelationshipTypeEnum.ENTITIES_DEBT_COLLECTION_AGENCY]: 'ein weiteres Inkassounternehmen',
  [RelationshipTypeEnum.ENTITIES_EXPERT_COMMITTEE]: 'einen weiteren Gutachterausschuss',
  [RelationshipTypeEnum.ENTITIES_ROAD_TRAFFIC_OFFICE]: 'ein weiteres Straßenverkehrsamt',
  [RelationshipTypeEnum.ENTITIES_CHILD]: 'ein weiteres Kind',
  [RelationshipTypeEnum.ENTITIES_PARENT]: 'ein weiteres Elternteil',
  [RelationshipTypeEnum.ENTITIES_SUPERVISORY_BOARD]: 'einen weiteren Aufsichtsrat',
  [RelationshipTypeEnum.ENTITIES_MEDICAL_SERVICE]: 'einen weiteren Medizinischen Dienst',
  [RelationshipTypeEnum.ENTITIES_EMPLOYMENT_AGENCY]: 'eine weitere Arbeitsagentur'
}

/**
 * German demonstrative pronoun phrases for the description.
 * Format: "diesen X" (m), "diese X" (f), "dieses X" (n)
 */
export const entityDemonstrativeDE: Record<string, string> = {
  // Masculine nouns
  [RelationshipTypeEnum.ENTITIES_CLIENT]: 'diesen Mandanten',
  [RelationshipTypeEnum.ENTITIES_OPPONENT]: 'diesen Gegner',
  [RelationshipTypeEnum.ENTITIES_LABOR_LAW_EMPLOYEE]: 'diesen Arbeitnehmer',
  [RelationshipTypeEnum.ENTITIES_LABOR_LAW_EMPLOYER]: 'diesen Arbeitgeber',
  [RelationshipTypeEnum.ENTITIES_AT_FAULT_DRIVER]: 'diesen Unfallverursacher',
  [RelationshipTypeEnum.ENTITIES_PLAINTIFF]: 'diesen Kläger',
  [RelationshipTypeEnum.ENTITIES_VEHICLE_OWNER]: 'diesen Fahrzeughalter',
  [RelationshipTypeEnum.ENTITIES_DRIVER]: 'diesen Fahrer',
  [RelationshipTypeEnum.ENTITIES_JUDGE]: 'diesen Richter',
  [RelationshipTypeEnum.ENTITIES_PROSECUTOR]: 'diesen Staatsanwalt',
  [RelationshipTypeEnum.ENTITIES_CO_PLAINTIFF]: 'diesen Mitkläger',
  [RelationshipTypeEnum.ENTITIES_TENANT]: 'diesen Mieter',
  [RelationshipTypeEnum.ENTITIES_LANDLORD]: 'diesen Vermieter',
  [RelationshipTypeEnum.ENTITIES_SUBTENANT]: 'diesen Untermieter',
  [RelationshipTypeEnum.ENTITIES_OWNER]: 'diesen Eigentümer',
  [RelationshipTypeEnum.ENTITIES_REAL_ESTATE_AGENT]: 'diesen Immobilienmakler',
  [RelationshipTypeEnum.ENTITIES_CO_OWNER]: 'diesen Miteigentümer',
  [RelationshipTypeEnum.ENTITIES_CO_TENANT]: 'diesen Mitmieter',
  [RelationshipTypeEnum.ENTITIES_COMPLAINANT]: 'diesen Anzeigenerstatter',
  [RelationshipTypeEnum.ENTITIES_APPLICANT]: 'diesen Antragsteller',
  [RelationshipTypeEnum.ENTITIES_RESPONDENT]: 'diesen Antwortsteller',
  [RelationshipTypeEnum.ENTITIES_PRINCIPAL_CLIENT]: 'diesen Auftraggeber',
  [RelationshipTypeEnum.ENTITIES_HUSBAND]: 'diesen Ehemann',
  [RelationshipTypeEnum.ENTITIES_KORRESPONDENCE_LAWYER]: 'diesen Korrespondenzanwalt',
  [RelationshipTypeEnum.ENTITIES_OPPOSING_LAWYER]: 'diesen Gegenanwalt',
  [RelationshipTypeEnum.ENTITIES_LAWYER]: 'diesen Anwalt',
  [RelationshipTypeEnum.ENTITIES_CONTACT_PERSON]: 'diesen Ansprechpartner',
  [RelationshipTypeEnum.ENTITIES_THIRD_PARTY_DEBTOR]: 'diesen Drittschuldner',
  [RelationshipTypeEnum.ENTITIES_BUYER]: 'diesen Käufer',
  [RelationshipTypeEnum.ENTITIES_SELLER]: 'diesen Verkäufer',
  [RelationshipTypeEnum.ENTITIES_ADMINISTRATOR]: 'diesen Verwalter',
  [RelationshipTypeEnum.ENTITIES_DEBTOR]: 'diesen Schuldner',
  [RelationshipTypeEnum.ENTITIES_CREDITOR]: 'diesen Gläubiger',
  [RelationshipTypeEnum.ENTITIES_LESSEE]: 'diesen Pächter',
  [RelationshipTypeEnum.ENTITIES_LESSOR]: 'diesen Verpächter',
  [RelationshipTypeEnum.ENTITIES_PRINCIPAL]: 'diesen Vollmachtgeber',
  [RelationshipTypeEnum.ENTITIES_TESTATOR]: 'diesen Erblasser',
  [RelationshipTypeEnum.ENTITIES_CURATOR_OF_THE_ESTATE]: 'diesen Nachlasspfleger',
  [RelationshipTypeEnum.ENTITIES_EXECUTOR_OF_THE_WILL]: 'diesen Testamentsvollstrecker',
  [RelationshipTypeEnum.ENTITIES_ESTATE_ADMINISTRATOR]: 'diesen Nachlassverwalter',
  [RelationshipTypeEnum.ENTITIES_SECONDARY_CLIENT]: 'diesen sekundären Mandanten',
  [RelationshipTypeEnum.ENTITIES_SECONDARY_OPPONENT]: 'diesen sekundären Gegner',
  [RelationshipTypeEnum.ENTITIES_INVESTIGATOR]: 'diesen Ermittler',
  [RelationshipTypeEnum.ENTITIES_BAILIFF]: 'diesen Gerichtsvollzieher',
  [RelationshipTypeEnum.ENTITIES_NOTARY]: 'diesen Notar',
  [RelationshipTypeEnum.ENTITIES_TRUSTEE]: 'diesen Treuhänder',
  [RelationshipTypeEnum.ENTITIES_INSOLVENCY_ADMINISTRATOR]: 'diesen Insolvenzverwalter',
  [RelationshipTypeEnum.ENTITIES_DEFENDANT_REPRESENTATIVE]: 'diesen Beklagtenvertreter',
  [RelationshipTypeEnum.ENTITIES_PLAINTIFF_REPRESENTATIVE]: 'diesen Klägervertreter',
  [RelationshipTypeEnum.ENTITIES_FRACTIONAL_OWNER]: 'diesen Bruchteilseigentümer',
  [RelationshipTypeEnum.ENTITIES_JOINT_DEBTOR]: 'diesen Gesamtschuldner',
  [RelationshipTypeEnum.ENTITIES_JOINT_CREDITOR]: 'diesen Gesamtgläubiger',
  [RelationshipTypeEnum.ENTITIES_APPLICATION_RESPONDENT]: 'diesen Antragsgegner',
  [RelationshipTypeEnum.ENTITIES_HEREDITARY_BUILDING_GRANTOR]: 'diesen Erbbaugeber',
  [RelationshipTypeEnum.ENTITIES_SPECIAL_PROPERTY_MANAGER]: 'diesen Sondereigentumsverwalter',
  [RelationshipTypeEnum.ENTITIES_HOUSE_MANAGER]: 'diesen Hausverwalter',
  [RelationshipTypeEnum.ENTITIES_APPELLANT]: 'diesen Berufungskläger',
  [RelationshipTypeEnum.ENTITIES_COUNTER_CLAIMANT]: 'diesen Widerkläger',
  [RelationshipTypeEnum.ENTITIES_DOCTOR]: 'diesen Arzt',
  [RelationshipTypeEnum.ENTITIES_GUARDIAN]: 'diesen Vormund',
  [RelationshipTypeEnum.ENTITIES_CARETAKER]: 'diesen Betreuer',
  [RelationshipTypeEnum.ENTITIES_MAINTENANCE_OBLIGOR]: 'diesen Unterhaltspflichtigen',
  [RelationshipTypeEnum.ENTITIES_ADMINISTRATIVE_ASSISTANT]: 'diesen Verwaltungshelfer',
  [RelationshipTypeEnum.ENTITIES_CASE_WORKER]: 'diesen Sachbearbeiter',
  [RelationshipTypeEnum.ENTITIES_MANAGING_DIRECTOR]: 'diesen Geschäftsführer',
  [RelationshipTypeEnum.ENTITIES_SHAREHOLDER]: 'diesen Gesellschafter',
  [RelationshipTypeEnum.ENTITIES_COMMERCIAL_AGENT]: 'diesen Handelsvertreter',
  [RelationshipTypeEnum.ENTITIES_LIMITED_PARTNER]: 'diesen Kommanditisten',
  [RelationshipTypeEnum.ENTITIES_AUTHORIZED_SIGNATORY]: 'diesen Prokuristen',
  [RelationshipTypeEnum.ENTITIES_REHABILITATION_PROVIDER]: 'diesen Rehabilitationsträger',
  [RelationshipTypeEnum.ENTITIES_LEGAL_ASSISTANT]: 'diesen Verfahrensbeistand',
  [RelationshipTypeEnum.ENTITIES_OFFICIANT]: 'diesen Amtsträger',
  [RelationshipTypeEnum.ENTITIES_PROBATION_OFFICER]: 'diesen Bewährungshelfer',

  // Masculine nouns - weak declension
  [RelationshipTypeEnum.ENTITIES_WITNESS]: 'diesen Zeugen',
  [RelationshipTypeEnum.ENTITIES_GUARANTOR]: 'diesen Bürgen',
  [RelationshipTypeEnum.ENTITIES_HEIR]: 'diesen Erben',
  [RelationshipTypeEnum.ENTITIES_PRELIMINARY_HEIR]: 'diesen Vorerben',
  [RelationshipTypeEnum.ENTITIES_SUBSEQUENT_HEIR]: 'diesen Nacherben',
  [RelationshipTypeEnum.ENTITIES_PATIENT]: 'diesen Patienten',

  // Masculine nouns - nominalized adjectives
  [RelationshipTypeEnum.ENTITIES_ACCUSED]: 'diesen Beschuldigten',
  [RelationshipTypeEnum.ENTITIES_DEFENDANT]: 'diesen Angeklagten',
  [RelationshipTypeEnum.ENTITIES_COMPLAINT]: 'diesen Beklagten',
  [RelationshipTypeEnum.ENTITIES_VICTIM]: 'diesen Geschädigten',
  [RelationshipTypeEnum.ENTITIES_VEHICLE_EXPERT]: 'diesen KFZ-Sachverständigen',
  [RelationshipTypeEnum.ENTITIES_POLICE_OFFICER]: 'diesen Polizeibeamten',
  [RelationshipTypeEnum.ENTITIES_EXPERT]: 'diesen Sachverständigen',
  [RelationshipTypeEnum.ENTITIES_HEREDITARY_LEASEHOLDER]: 'diesen Erbbauberechtigten',
  [RelationshipTypeEnum.ENTITIES_AUTHORIZED_REPRESENTATIVE]: 'diesen Bevollmächtigten',
  [RelationshipTypeEnum.ENTITIES_COMPULSORY_PORTION_BENEFICIARY]: 'diesen Pflichtteilsberechtigten',
  [RelationshipTypeEnum.ENTITIES_APPELLEE]: 'diesen Berufungsbeklagten',
  [RelationshipTypeEnum.ENTITIES_COUNTER_DEFENDANT]: 'diesen Widerbeklagten',
  [RelationshipTypeEnum.ENTITIES_LEGAL_GUARDIAN]: 'diesen Sorgeberechtigten',
  [RelationshipTypeEnum.ENTITIES_BENEFICIARY]: 'diesen Leistungsberechtigten',
  [RelationshipTypeEnum.ENTITIES_AFFECTED_PERSON]: 'diesen Betroffenen',
  [RelationshipTypeEnum.ENTITIES_VICTIM_DRIVER]: 'diesen geschädigten Fahrer',
  [RelationshipTypeEnum.ENTITIES_OPPOSING_COUNSEL]: 'diesen gegnerischen Anwalt',
  [RelationshipTypeEnum.ENTITIES_LEGAL_REPRESENTATIVE]: 'diesen gesetzlichen Vertreter',

  // Feminine nouns (diese)
  [RelationshipTypeEnum.ENTITIES_AGENCY]: 'diese Behörde',
  [RelationshipTypeEnum.ENTITIES_FOREIGNERS_AUTHORITY]: 'diese Ausländerbehörde',
  [RelationshipTypeEnum.ENTITIES_INSURANCE]: 'diese Versicherung',
  [RelationshipTypeEnum.ENTITIES_POLICE_STATION]: 'diese Polizeidienststelle',
  [RelationshipTypeEnum.ENTITIES_PUBLIC_PROSECUTOR]: 'diese Staatsanwaltschaft',
  [RelationshipTypeEnum.ENTITIES_PROPERTY_MANAGEMENT]: 'diese Hausverwaltung',
  [RelationshipTypeEnum.ENTITIES_TENANTS_ASSOCIATION]: 'diesen Mieterverein',
  [RelationshipTypeEnum.ENTITIES_PROPERTY_OWNERS_ASSOCIATION]: 'diese Eigentümergemeinschaft',
  [RelationshipTypeEnum.ENTITIES_WIFE]: 'diese Ehefrau',
  [RelationshipTypeEnum.ENTITIES_LEGAL_EXPENSES_INSURANCE]: 'diese Rechtsschutzversicherung',
  [RelationshipTypeEnum.ENTITIES_LIABILITY_INSURANCE]: 'diese Haftpflichtversicherung',
  [RelationshipTypeEnum.ENTITIES_BANK]: 'diese Bank',
  [RelationshipTypeEnum.ENTITIES_GENERAL_PROSECUTOR]: 'diese Generalstaatsanwaltschaft',
  [RelationshipTypeEnum.ENTITIES_MUNICIPALITY]: 'diese Gemeinde',
  [RelationshipTypeEnum.ENTITIES_COURT_COMPLAINT]: 'diese Gerichtsbeschwerde',
  [RelationshipTypeEnum.ENTITIES_FINE_AUTHORITY]: 'diese Bußgeldbehörde',
  [RelationshipTypeEnum.ENTITIES_COMMUNITY_OF_HOMEOWNERS]:
    'diese Gemeinschaft der Wohnungseigentümer (GdWE)',
  [RelationshipTypeEnum.ENTITIES_PROFESSIONAL_LIABILITY_INSURANCE]:
    'diese Berufshaftpflichtversicherung',
  [RelationshipTypeEnum.ENTITIES_BUILDING_OWNERS_ASSOCIATION]: 'diese Bauherrengemeinschaft',
  [RelationshipTypeEnum.ENTITIES_LANDLORD_ASSOCIATION]: 'diesen Vermieterverein',
  [RelationshipTypeEnum.ENTITIES_LANDLORD_COMMUNITY]: 'diese Vermietergemeinschaft',
  [RelationshipTypeEnum.ENTITIES_FOSTER_PARENT]: 'diese Pflegeperson',
  [RelationshipTypeEnum.ENTITIES_PENSION_INSURANCE]: 'diese Rentenversicherung',
  [RelationshipTypeEnum.ENTITIES_HEALTH_INSURANCE]: 'diese Krankenkasse',
  [RelationshipTypeEnum.ENTITIES_TAX_AUTHORITY]: 'diese Finanzbehörde',
  [RelationshipTypeEnum.ENTITIES_OPPONENT_INSURANCE]: 'diese gegnerische Versicherung',
  [RelationshipTypeEnum.ENTITIES_MEDICAL_INSTITUTION]: 'diese medizinische Einrichtung',
  [RelationshipTypeEnum.ENTITIES_WORKSHOP]: 'diese Werkstatt',
  [RelationshipTypeEnum.ENTITIES_EMPLOYMENT_AGENCY]: 'diese Arbeitsagentur',

  // Neuter nouns (dieses)
  [RelationshipTypeEnum.ENTITIES_OTHER]: 'diesen Sonstigen',
  [RelationshipTypeEnum.ENTITIES_COURT]: 'dieses Gericht',
  [RelationshipTypeEnum.ENTITIES_FIRST_INSTANCE_COURT]: 'dieses Gericht 1. Instanz',
  [RelationshipTypeEnum.ENTITIES_SECOND_INSTANCE_COURT]: 'dieses Gericht 2. Instanz',
  [RelationshipTypeEnum.ENTITIES_THIRD_INSTANCE_COURT]: 'dieses Gericht 3. Instanz',
  [RelationshipTypeEnum.ENTITIES_TAX_OFFICE]: 'dieses Finanzamt',
  [RelationshipTypeEnum.ENTITIES_LAND_REGISTRY]: 'dieses Grundbuchamt',
  [RelationshipTypeEnum.ENTITIES_DUNNING_COURT]: 'dieses Mahngericht',
  [RelationshipTypeEnum.ENTITIES_ENFORCEMENT_COURT]: 'dieses Vollstreckungsgericht',
  [RelationshipTypeEnum.ENTITIES_REGISTER_COURT]: 'dieses Registergericht',
  [RelationshipTypeEnum.ENTITIES_PROBATE_COURT]: 'dieses Nachlassgericht',
  [RelationshipTypeEnum.ENTITIES_REGISTRY_OFFICE]: 'dieses Standesamt',
  [RelationshipTypeEnum.ENTITIES_LEGAL_AID_COURT]: 'dieses Beratungshilfegericht',
  [RelationshipTypeEnum.ENTITIES_RESIDENT_REGISTRATION_OFFICE]: 'dieses Einwohnermeldeamt',
  [RelationshipTypeEnum.ENTITIES_INSOLVENCY_COURT]: 'dieses Insolvenzgericht',
  [RelationshipTypeEnum.ENTITIES_INTEGRATION_OFFICE]: 'dieses Integrationsamt',
  [RelationshipTypeEnum.ENTITIES_EXECUTION_ORGAN]: 'dieses Vollstreckungsorgan',
  [RelationshipTypeEnum.ENTITIES_YOUTH_WELFARE_OFFICE]: 'dieses Jugendamt',
  [RelationshipTypeEnum.ENTITIES_SOCIAL_SECURITY_OFFICE]: 'dieses Sozialamt',
  [RelationshipTypeEnum.ENTITIES_JOB_CENTER]: 'dieses Jobcenter',
  [RelationshipTypeEnum.ENTITIES_DISABILITY_OFFICE]: 'dieses Versorgungsamt',
  [RelationshipTypeEnum.ENTITIES_SOCIAL_COURT]: 'dieses Sozialgericht',
  [RelationshipTypeEnum.ENTITIES_DEBT_COLLECTION_AGENCY]: 'dieses Inkassounternehmen',
  [RelationshipTypeEnum.ENTITIES_EXPERT_COMMITTEE]: 'diesen Gutachterausschuss',
  [RelationshipTypeEnum.ENTITIES_ROAD_TRAFFIC_OFFICE]: 'dieses Straßenverkehrsamt',
  [RelationshipTypeEnum.ENTITIES_CHILD]: 'dieses Kind',
  [RelationshipTypeEnum.ENTITIES_PARENT]: 'dieses Elternteil',
  [RelationshipTypeEnum.ENTITIES_SUPERVISORY_BOARD]: 'diesen Aufsichtsrat',
  [RelationshipTypeEnum.ENTITIES_MEDICAL_SERVICE]: 'diesen Medizinischen Dienst'
}

/**
 * Helper function to get the accusative phrase for an entity type.
 * Falls back to a generic phrase if the entity is not in the mapping.
 */
export function getEntityAccusativeDE(participantType: string): string {
  return entityAccusativeDE[participantType] || `einen weiteren ${participantType}`
}

/**
 * Helper function to get the demonstrative phrase for an entity type.
 * Falls back to a generic phrase if the entity is not in the mapping.
 */
export function getEntityDemonstrativeDE(participantType: string): string {
  return entityDemonstrativeDE[participantType] || `diesen ${participantType}`
}
